import { useMemo, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, ChevronDown } from "lucide-react";

// Full match replay: step through every leg and every turn.
export default function MatchReplay({ match }) {
  const is501 = match.game_type === "501";
  const legs = match.legs || [];

  // Flatten turns across legs, tracking a running state we can "replay".
  const steps = useMemo(() => {
    const out = [];
    legs.forEach((leg, li) => {
      const remaining = {};
      if (is501) match.player_ids.forEach((pid) => (remaining[pid] = 501));
      const progress = {};
      if (!is501) match.player_ids.forEach((pid) => (progress[pid] = 0));
      (leg.turns || []).forEach((t, ti) => {
        if (is501) {
          remaining[t.player_id] = t.remaining_after;
        } else {
          progress[t.player_id] = (progress[t.player_id] || 0) + (t.hits || 0);
        }
        out.push({
          legIndex: li,
          legNumber: leg.leg_number,
          turnIndex: ti,
          turn: t,
          snapshot: { ...(is501 ? remaining : progress) },
          winner_id: leg.winner_id,
        });
      });
    });
    return out;
  }, [legs, is501, match.player_ids]);

  const [ptr, setPtr] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);

  const stop = () => {
    setPlaying(false);
    if (timer.current) clearInterval(timer.current);
  };

  const play = () => {
    if (steps.length === 0) return;
    setPlaying(true);
    setPtr((p) => (p >= steps.length - 1 ? -1 : p));
    timer.current = setInterval(() => {
      setPtr((p) => {
        if (p >= steps.length - 1) {
          stop();
          return p;
        }
        return p + 1;
      });
    }, 900);
  };

  const toggle = () => (playing ? stop() : play());
  const step = (d) => {
    stop();
    setPtr((p) => Math.max(-1, Math.min(steps.length - 1, p + d)));
  };

  const cur = ptr >= 0 ? steps[ptr] : null;
  const names = match.player_names;

  if (legs.length === 0) return null;

  return (
    <div data-testid="match-replay">
      <h2 className="font-display text-3xl font-black uppercase text-white mb-1">Match Replay</h2>
      <p className="text-slate-400 text-sm mb-4">
        Step through every {is501 ? "leg and turn" : "round and dart"} of this match.
      </p>

      {/* Replay controls + current throw readout */}
      <div className="panel rounded-sm p-4 mb-4">
        <div className="flex items-center gap-1 mb-4">
          <button
            data-testid="replay-back-btn"
            onClick={() => step(-1)}
            className="p-2.5 panel rounded-sm text-slate-300 hover:text-white"
          >
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            data-testid="replay-play-btn"
            onClick={toggle}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase text-sm tracking-wide rounded-sm transition-colors"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {playing ? "Pause" : ptr >= steps.length - 1 ? "Replay" : "Play"}
          </button>
          <button
            data-testid="replay-fwd-btn"
            onClick={() => step(1)}
            className="p-2.5 panel rounded-sm text-slate-300 hover:text-white"
          >
            <SkipForward className="w-4 h-4" />
          </button>
        </div>

        <input
          type="range"
          min={-1}
          max={steps.length - 1}
          value={ptr}
          onChange={(e) => {
            stop();
            setPtr(parseInt(e.target.value, 10));
          }}
          className="w-full accent-[#007AFF]"
          data-testid="replay-scrubber"
        />

        {cur ? (
          <div className="mt-4 text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF3B30]">
              {is501 ? "Leg" : "Round"} {cur.legNumber}
            </div>
            <div className="font-display text-3xl font-black text-white leading-none mt-1">
              {names[cur.turn.player_id]}
            </div>
            <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
              {(cur.turn.darts || []).map((d, i) => (
                <span
                  key={i}
                  className={`px-3 py-1 rounded-sm font-display text-lg font-bold border ${
                    is501
                      ? "border-[#007AFF] text-white"
                      : d.hit
                      ? "border-[#34C759] text-[#34C759]"
                      : "border-[#FF3B30] text-[#FF3B30]"
                  }`}
                >
                  {is501 ? d.label : `${d.target}${d.hit ? " ✓" : " ✗"}`}
                </span>
              ))}
            </div>
            {is501 && (
              <div className="font-display text-lg font-bold text-[#FFCC00] mt-2">
                {cur.turn.busted ? "BUST" : `Scored ${cur.turn.turn_score}`} · Left{" "}
                {cur.snapshot[cur.turn.player_id]}
                {cur.turn.is_checkout && " · CHECKOUT!"}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 text-center text-slate-500 text-sm">Press play to replay the match</div>
        )}
      </div>

      {/* Leg-by-leg tables */}
      <div className="space-y-1">
        {legs.map((leg, li) => (
          <LegBlock
            key={li}
            leg={leg}
            names={names}
            is501={is501}
            highlight={cur && cur.legIndex === li ? cur.turnIndex : -1}
          />
        ))}
      </div>
    </div>
  );
}

function LegBlock({ leg, names, is501, highlight }) {
  const [open, setOpen] = useState(highlight >= 0);

  return (
    <div className="panel rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-3 hover:bg-[#161616] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-display text-lg font-black text-[#FF3B30]">
            {is501 ? "LEG" : "ROUND"} {leg.leg_number}
          </span>
          <span className="text-sm font-semibold text-white">
            {names[leg.winner_id]} <span className="text-[#FFCC00]">won</span>
          </span>
          {leg.duration_ms ? (
            <span className="text-xs text-slate-500">{(leg.duration_ms / 1000).toFixed(1)}s</span>
          ) : null}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#262626] overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {(leg.turns || []).map((t, ti) => (
                <tr
                  key={ti}
                  className={`border-b border-[#1a1a1a] ${
                    highlight === ti ? "bg-[#0d1a2b]" : ""
                  }`}
                >
                  <td className="p-2.5 font-semibold text-white w-1/3">{names[t.player_id]}</td>
                  <td className="p-2.5">
                    <div className="flex gap-1.5 flex-wrap">
                      {(t.darts || []).map((d, i) => (
                        <span
                          key={i}
                          className={`px-2 py-0.5 rounded-sm text-xs font-bold ${
                            is501
                              ? "bg-[#1f1f1f] text-slate-200"
                              : d.hit
                              ? "bg-[#12331d] text-[#34C759]"
                              : "bg-[#331212] text-[#FF3B30]"
                          }`}
                        >
                          {is501 ? d.label : `${d.target}${d.hit ? "✓" : "✗"}`}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-2.5 text-right font-display font-bold text-white w-24">
                    {is501
                      ? t.busted
                        ? "BUST"
                        : `${t.turn_score} → ${t.remaining_after}`
                      : `${t.hits} hit`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
